import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router';
import { MODULE_CATALOG } from '@/shell/model/module-catalog';
import { useFinanceCapability } from './useFinanceCapability';

const FINANCE_INDEX = '/finance';

/**
 * Where `/finance` should land for a caller who may not see the dashboard: the first Finance
 * screen, in the rail's own order, whose permission they hold. `null` when the dashboard is theirs
 * or when no screen is.
 *
 * The rail's entry for Finance always points at `/finance`, the dashboard. That was harmless while
 * every Finance role held the dashboard, and stopped being so when Human decision 27 gave the
 * accountant three screens that do not include it: opening Finance showed a screen whose data the
 * service refuses. Decision 57's role, which holds the purchases VAT register alone, would open
 * onto the same refusal. The order is read from the catalog rather than restated here, so the
 * screen Finance opens onto is always the first one its rail offers.
 */
export function financeLanding(permissions: ReadonlySet<string>): string | null {
  const finance = MODULE_CATALOG.find((module) => module.id === 'finance');
  const first = finance?.sections.find(
    (section) =>
      section.gate?.kind === 'permission' &&
      section.gate.anyOf.some((code) => permissions.has(code)),
  );
  return first === undefined || first.path === FINANCE_INDEX ? null : first.path;
}

export function FinanceGate() {
  const { t } = useTranslation('finance');
  const capability = useFinanceCapability({ requireActiveContext: true });
  const { pathname } = useLocation();

  if (capability.status === 'checking') {
    return <p className="p-6 text-sm">{t('gate.checkingContext')}</p>;
  }

  if (capability.status === 'load-error') {
    return (
      <div
        className="border-border bg-card text-foreground mx-auto mt-6 flex max-w-md items-center
          gap-3 rounded-lg border p-4 text-sm"
      >
        <CircleAlert className="text-destructive size-5 shrink-0" aria-hidden="true" />
        <span>{t('gate.loadError')}</span>
      </div>
    );
  }

  if (capability.status === 'no-context') {
    return <p className="p-6 text-sm text-foreground/60">{t('gate.noContext')}</p>;
  }

  if (capability.status === 'no-access') {
    return <p className="p-6 text-sm text-foreground/60">{t('gate.noAccess')}</p>;
  }

  if (pathname.replace(/\/+$/, '') === FINANCE_INDEX) {
    const landing = financeLanding(capability.permissions);
    if (landing !== null) {
      return <Navigate to={landing} replace />;
    }
  }

  return <Outlet />;
}
